import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SplashBloxBounce from './SplashBloxBounce';
import SplashParticleBurst from './SplashParticleBurst';
import SplashRetroScan from './SplashRetroScan';
import SplashCinematic from './SplashCinematic';
import { readSplashVariant, SPLASH_MIN_MS, getSplashBloxTiming } from './splashConstants';
import { playSplashSting } from './splashSound';
import './splash.css';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(!!mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);
  return reduced;
}

const VARIANT_MAP = {
  blox: SplashBloxBounce,
  bounce: SplashBloxBounce,
  particles: SplashParticleBurst,
  retro: SplashRetroScan,
  cinematic: SplashCinematic,
};

/**
 * Full-screen intro. Honors min display time and reduced motion.
 * @param {{ variant?: import('./splashConstants').SplashVariantId; onComplete: () => void; playSound?: boolean }} props
 */
export default function SplashScreen({ variant: variantProp, onComplete, playSound = true }) {
  const reducedMotion = usePrefersReducedMotion();
  const variant = variantProp || readSplashVariant();
  const isBlox = variant === 'blox' || variant === 'bounce';
  const VariantView = VARIANT_MAP[variant] || SplashBloxBounce;
  const [ready, setReady] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [animSettled, setAnimSettled] = useState(!isBlox || reducedMotion);

  const handleSettled = useCallback(() => {
    setAnimSettled(true);
  }, []);

  useEffect(() => {
    if (playSound && !reducedMotion && !isBlox) {
      playSplashSting();
    }

    const minMs = reducedMotion ? 400 : isBlox ? getSplashBloxTiming().minMs : SPLASH_MIN_MS;
    const id = window.setTimeout(() => setMinElapsed(true), minMs);
    return () => window.clearTimeout(id);
  }, [playSound, reducedMotion, isBlox]);

  /** Never block the app if blox onSettled fails (gesture/timeline edge cases). */
  useEffect(() => {
    if (!isBlox || reducedMotion) return undefined;
    const maxMs = getSplashBloxTiming().minMs + 8000;
    const id = window.setTimeout(() => setAnimSettled(true), maxMs);
    return () => window.clearTimeout(id);
  }, [isBlox, reducedMotion]);

  useEffect(() => {
    if (minElapsed && animSettled) setReady(true);
  }, [minElapsed, animSettled]);

  useEffect(() => {
    if (!ready) return undefined;
    const id = requestAnimationFrame(() => onComplete());
    return () => cancelAnimationFrame(id);
  }, [ready, onComplete]);

  const label = useMemo(() => {
    const names = {
      blox: 'Blox bounce',
      bounce: 'Blox bounce',
      particles: 'Particle burst',
      retro: 'Retro scan',
      cinematic: 'Cinematic',
    };
    return names[variant] || 'ZFLIX';
  }, [variant]);

  return (
    <div
      className={`zflix-splash-root${reducedMotion ? ' zflix-splash-root--reduced' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`Loading ZFLIX, ${label} intro`}
      data-zflix-splash-variant={variant}
    >
      <VariantView
        reducedMotion={reducedMotion}
        playSound={playSound && !reducedMotion}
        onSettled={isBlox ? handleSettled : undefined}
      />
    </div>
  );
}
